import {
	PointerEvent as ReactPointerEvent,
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, PanInfo, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ToastCard, ToastViewport } from '@quizzach/styles';
import type { ToastSeverity } from '@quizzach/styles';
import {
	IcAlertRound,
	IcAlertTriangle,
	IcCheck,
	IcClose,
	IcInfo,
} from '@quizzach/assets';

const TOAST_DURATION_MS = 4000;
const TOAST_ACTION_DURATION_MS = 6000;
const TOAST_ERROR_DURATION_MS = 8000;
const SWIPE_DISMISS_PX = 80;
const SWIPE_DISMISS_VELOCITY = 500;

const severityIcon = {
	error: IcAlertRound,
	warning: IcAlertTriangle,
	info: IcInfo,
} as const;

interface ToastOptions {
	message: string;
	/** Second, quieter line, e.g. the quiz that was just added. */
	description?: string;
	severity?: ToastSeverity;
	actionLabel?: string;
	onAction?: () => void;
}

interface ToastEntry extends ToastOptions {
	id: number;
}

interface ToastTimer {
	handle: number | null;
	remaining: number;
	startedAt: number;
}

// errors get the longest read, and a toast with a button time to reach it
const durationOf = ({ severity, actionLabel }: ToastOptions) =>
	severity === 'error'
		? TOAST_ERROR_DURATION_MS
		: actionLabel
			? TOAST_ACTION_DURATION_MS
			: TOAST_DURATION_MS;

interface ToastContextValue {
	showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue>({
	showToast: () => undefined,
});

export const useToast = () => useContext(ToastContext);

/**
 * Non-blocking toast stack: bottom-right on desktop, above the bottom nav
 * on mobile. Pills with a severity icon in a tinted circle, a message, an
 * optional second line and an optional action that dismisses on tap. The
 * timer pauses on hover and while swiping; a swipe sideways dismisses, and
 * errors also get a close button.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
	const { t } = useTranslation();
	const [toasts, setToasts] = useState<ToastEntry[]>([]);
	// source of truth lives in refs so timer side effects stay out of state
	// updaters (StrictMode double-invokes those); state only mirrors for render
	const toastsRef = useRef<ToastEntry[]>([]);
	const timersRef = useRef(new Map<number, ToastTimer>());
	const idRef = useRef(0);

	const commit = useCallback(() => {
		setToasts([...toastsRef.current]);
	}, []);

	const dismiss = useCallback(
		(id: number) => {
			const timer = timersRef.current.get(id);
			if (timer) {
				if (timer.handle !== null) window.clearTimeout(timer.handle);
				timersRef.current.delete(id);
			}
			toastsRef.current = toastsRef.current.filter((toast) => toast.id !== id);
			commit();
		},
		[commit]
	);

	const startTimer = useCallback(
		(id: number, ms: number) => {
			const previous = timersRef.current.get(id);
			if (previous?.handle != null) window.clearTimeout(previous.handle);
			timersRef.current.set(id, {
				handle: window.setTimeout(() => dismiss(id), ms),
				remaining: ms,
				startedAt: Date.now(),
			});
		},
		[dismiss]
	);

	const pauseTimer = useCallback((id: number) => {
		const timer = timersRef.current.get(id);
		if (!timer || timer.handle === null) return;
		window.clearTimeout(timer.handle);
		timer.handle = null;
		timer.remaining -= Date.now() - timer.startedAt;
	}, []);

	const resumeTimer = useCallback(
		(id: number) => {
			const timer = timersRef.current.get(id);
			if (!timer || timer.handle !== null) return;
			startTimer(id, Math.max(timer.remaining, 800));
		},
		[startTimer]
	);

	const showToast = useCallback(
		(options: ToastOptions) => {
			const severity = options.severity ?? 'info';
			const existing = toastsRef.current.find(
				(toast) =>
					toast.message === options.message &&
					(toast.severity ?? 'info') === severity
			);

			// an identical toast is already up (e.g. repeated failing clicks):
			// restart its timer and update it instead of stacking a duplicate
			if (existing) {
				startTimer(existing.id, durationOf(options));
				toastsRef.current = toastsRef.current.map((toast) =>
					toast.id === existing.id ? { ...options, id: toast.id } : toast
				);
				commit();
				return;
			}

			idRef.current += 1;
			const id = idRef.current;
			toastsRef.current = [...toastsRef.current, { ...options, id }];
			startTimer(id, durationOf(options));
			commit();
		},
		[commit, startTimer]
	);

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			{createPortal(
				<ToastViewport aria-live='polite'>
					<AnimatePresence>
						{toasts.map((toast) => {
							const severity = toast.severity ?? 'info';
							const Icon =
								severity === 'success' ? IcCheck : severityIcon[severity];

							return (
								<ToastCard
									key={toast.id}
									$severity={severity}
									className={toast.actionLabel ? 'with-action' : undefined}
									role={severity === 'error' ? 'alert' : undefined}
									as={motion.div}
									initial={{ opacity: 0, y: 16, scale: 0.97 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: 8, scale: 0.97 }}
									transition={{ duration: 0.24, ease: [0.2, 0.65, 0.2, 1] }}
									layout
									drag='x'
									dragSnapToOrigin
									dragElastic={0.6}
									dragMomentum={false}
									// only a real mouse: a tap on a phone would never "leave"
									onPointerEnter={(event: ReactPointerEvent) => {
										if (event.pointerType === 'mouse') pauseTimer(toast.id);
									}}
									onPointerLeave={(event: ReactPointerEvent) => {
										if (event.pointerType === 'mouse') resumeTimer(toast.id);
									}}
									onDragStart={() => pauseTimer(toast.id)}
									onDragEnd={(_event: unknown, info: PanInfo) => {
										if (
											Math.abs(info.offset.x) > SWIPE_DISMISS_PX ||
											Math.abs(info.velocity.x) > SWIPE_DISMISS_VELOCITY
										) {
											dismiss(toast.id);
										} else {
											resumeTimer(toast.id);
										}
									}}
								>
									<span className='t-ic'>
										<Icon size={18} />
									</span>
									<span className='t-msg'>
										<b>{toast.message}</b>
										{toast.description && <small>{toast.description}</small>}
									</span>
									{toast.actionLabel && (
										<button
											type='button'
											className='t-action'
											onClick={() => {
												toast.onAction?.();
												dismiss(toast.id);
											}}
										>
											{toast.actionLabel}
										</button>
									)}
									{severity === 'error' && (
										<button
											type='button'
											className='t-x'
											aria-label={t('common.close')}
											onClick={() => dismiss(toast.id)}
										>
											<IcClose size={13} />
										</button>
									)}
								</ToastCard>
							);
						})}
					</AnimatePresence>
				</ToastViewport>,
				document.body
			)}
		</ToastContext.Provider>
	);
};
